import type { GameState, Strike, StrikeAssignment } from '../types/game-state';
import type { AIDecision, AIStrategy } from '../types/ai';
import { getReadyWeapons } from './arsenal';

const COUNTERFORCE_STRATEGY: AIStrategy = {
  type: 'counterforce',
  aggressionLevel: 0.6,
  firstStrikeThreshold: 2,
  targetPreferences: [
    { targetType: 'installation', installationType: 'icbm_silo', priority: 10, minimumWeaponsPerTarget: 2 },
    { targetType: 'installation', installationType: 'command_center', priority: 9, minimumWeaponsPerTarget: 2 },
    { targetType: 'installation', installationType: 'air_base', priority: 8, minimumWeaponsPerTarget: 1 },
    { targetType: 'installation', installationType: 'submarine_base', priority: 7, minimumWeaponsPerTarget: 1 },
    { targetType: 'city', priority: 3, minimumWeaponsPerTarget: 1 },
  ],
};

const COUNTERVALUE_STRATEGY: AIStrategy = {
  type: 'countervalue',
  aggressionLevel: 0.8,
  firstStrikeThreshold: 2,
  targetPreferences: [
    { targetType: 'city', priority: 10, minimumWeaponsPerTarget: 1 },
    { targetType: 'installation', installationType: 'command_center', priority: 5, minimumWeaponsPerTarget: 1 },
  ],
};

function selectStrategy(state: GameState): AIStrategy {
  // Escalation: after round 2 or if cities have been hit, switch to countervalue
  const playerStruckCities = state.strikeHistory.some(record =>
    record.attackerSide === state.playerSide &&
    record.results.some(r => r.targetType === 'city' && r.casualties > 100_000)
  );

  if (playerStruckCities || state.currentRound >= 3) {
    return COUNTERVALUE_STRATEGY;
  }
  return COUNTERFORCE_STRATEGY;
}

function computeAIDecision(state: GameState): AIDecision {
  const aiSide = state.playerSide === 'US' ? 'USSR' : 'US';
  const aiArsenal = aiSide === 'US' ? state.usArsenal : state.ussrArsenal;
  const readyWeapons = getReadyWeapons(aiArsenal);

  if (readyWeapons.length === 0) {
    return {
      shouldStrike: false,
      strike: null,
      reasoning: 'ALL STRATEGIC ASSETS DEPLETED. NO RETALIATION POSSIBLE.',
    };
  }

  const strategy = selectStrategy(state);
  const assignments: StrikeAssignment[] = [];

  // Hold back ~30% of SLBMs as reserve
  const slbms = readyWeapons.filter(w => w.system.type === 'slbm');
  const nonSlbms = readyWeapons.filter(w => w.system.type !== 'slbm');
  const slbmReserve = Math.floor(slbms.length * 0.3);
  const availableSlbms = slbms.slice(slbmReserve);
  const availableWeapons = [...nonSlbms, ...availableSlbms];

  // Use a fraction of available weapons (escalation ratchet)
  const usageFraction = Math.min(1, 0.4 + state.currentRound * 0.15);
  const weaponsToUse = availableWeapons.slice(0, Math.ceil(availableWeapons.length * usageFraction));

  // Get targets based on strategy
  const playerCities = state.playerSide === 'US' ? state.world.usCities : state.world.ussrCities;
  const playerInstallations = state.playerSide === 'US' ? state.world.usInstallations : state.world.ussrInstallations;

  const sortedPrefs = [...strategy.targetPreferences].sort((a, b) => b.priority - a.priority);
  let weaponIndex = 0;

  for (const pref of sortedPrefs) {
    if (weaponIndex >= weaponsToUse.length) break;

    if (pref.targetType === 'installation') {
      const targets = playerInstallations
        .filter(i => !i.isDestroyed && (!pref.installationType || i.type === pref.installationType));

      for (const target of targets) {
        for (let w = 0; w < pref.minimumWeaponsPerTarget && weaponIndex < weaponsToUse.length; w++) {
          const weapon = weaponsToUse[weaponIndex];
          if (weapon) {
            assignments.push({
              weaponId: weapon.id,
              targetId: target.id,
              targetType: 'installation',
            });
            weaponIndex++;
          }
        }
      }
    } else {
      // Target cities by population (descending)
      const targets = [...playerCities]
        .filter(c => !c.isDestroyed)
        .sort((a, b) => b.currentPopulation - a.currentPopulation);

      for (const target of targets) {
        if (weaponIndex >= weaponsToUse.length) break;
        const weapon = weaponsToUse[weaponIndex];
        if (weapon) {
          assignments.push({
            weaponId: weapon.id,
            targetId: target.id,
            targetType: 'city',
          });
          weaponIndex++;
        }
      }
    }
  }

  if (assignments.length === 0) {
    return {
      shouldStrike: false,
      strike: null,
      reasoning: 'NO VIABLE TARGETS REMAINING.',
    };
  }

  const strike: Strike = {
    attackerSide: aiSide,
    round: state.currentRound,
    assignments,
  };

  const cityTargets = assignments.filter(a => a.targetType === 'city').length;
  const installTargets = assignments.filter(a => a.targetType === 'installation').length;

  const reasoning = [
    `STRATEGY: ${strategy.type.toUpperCase()}`,
    `WEAPONS ALLOCATED: ${assignments.length} OF ${readyWeapons.length} READY`,
    `RESERVES HELD: ${slbmReserve} SLBM`,
    installTargets > 0 ? `MILITARY TARGETS: ${installTargets}` : null,
    cityTargets > 0 ? `POPULATION CENTERS: ${cityTargets}` : null,
    `AGGRESSION LEVEL: ${(strategy.aggressionLevel * 100).toFixed(0)}%`,
  ].filter(Boolean).join('\n');

  return {
    shouldStrike: true,
    strike,
    reasoning,
  };
}

export { computeAIDecision };
